import { useState, useEffect, useCallback } from "react";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { toast } from "sonner";
import {
  Search,
  Eye,
  Trash2,
  Phone,
  Mail,
  Calendar,
  MessageSquare,
} from "lucide-react";
import { adminApi } from "../../lib/adminApi";

export const AdminContacts = () => {
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedContact, setSelectedContact] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  const fetchContacts = useCallback(async () => {
    try {
      const response = await adminApi.get("/admin/contacts");
      setContacts(response.data.contacts);
      setTotal(response.data.total);
    } catch (error) {
      toast.error("Failed to fetch contacts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  const handleDelete = async (contactId) => {
    if (!confirm("Are you sure you want to delete this message?")) return;

    try {
      await adminApi.delete(`/admin/contacts/${contactId}`);
      toast.success("Message deleted successfully");
      fetchContacts();
      setIsDetailOpen(false);
    } catch (error) {
      toast.error("Failed to delete message");
    }
  };

  const filteredContacts = contacts.filter(
    (contact) =>
      contact.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.subject?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const openDetail = (contact) => {
    setSelectedContact(contact);
    setIsDetailOpen(true);
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-gray-200 rounded w-48"></div>
        <div className="h-12 bg-gray-200 rounded"></div>
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-20 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold text-gray-900 uppercase">
          Contact Messages
        </h1>
        <p className="text-gray-600 mt-1">
          {total} total messages received
        </p>
      </div>

      {/* Search */}
      <Card className="p-4 bg-white border-0 shadow-sm">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            placeholder="Search by name, email, or subject..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </Card>

      {/* Messages List */}
      <Card className="bg-white border-0 shadow-sm overflow-hidden">
        {filteredContacts.length === 0 ? (
          <div className="p-12 text-center">
            <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No messages found.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filteredContacts.map((contact) => (
              <div
                key={contact.id}
                className="p-6 hover:bg-gray-50 cursor-pointer transition-colors"
                onClick={() => openDetail(contact)}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-medium text-gray-900">{contact.name}</h3>
                      <span className="text-sm text-gray-500">{contact.email}</span>
                    </div>
                    <p className="font-medium text-gray-800 mb-1">{contact.subject}</p>
                    <p className="text-sm text-gray-500 line-clamp-2">
                      {contact.message}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-gray-400 whitespace-nowrap">
                      {new Date(contact.created_at).toLocaleDateString()}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(contact.id);
                      }}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Contact Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase">
              Message Details
            </DialogTitle>
          </DialogHeader>

          {selectedContact && (
            <div className="space-y-6">
              {/* Contact Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-xs text-gray-500 uppercase">Name</p>
                  <p className="font-medium">{selectedContact.name}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-gray-500 uppercase">Email</p>
                  <a
                    href={`mailto:${selectedContact.email}`}
                    className="font-medium text-brand-orange hover:underline flex items-center gap-1"
                  >
                    <Mail className="w-4 h-4" />
                    {selectedContact.email}
                  </a>
                </div>
                {selectedContact.phone && (
                  <div className="space-y-1 col-span-2">
                    <p className="text-xs text-gray-500 uppercase">Phone</p>
                    <a
                      href={`tel:${selectedContact.phone}`}
                      className="font-medium text-brand-orange hover:underline flex items-center gap-1"
                    >
                      <Phone className="w-4 h-4" />
                      {selectedContact.phone}
                    </a>
                  </div>
                )}
              </div>

              {/* Subject */}
              <div className="space-y-1">
                <p className="text-xs text-gray-500 uppercase">Subject</p>
                <p className="font-medium text-gray-900">{selectedContact.subject}</p>
              </div>

              {/* Message */}
              <div className="space-y-1">
                <p className="text-xs text-gray-500 uppercase">Message</p>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-gray-700 whitespace-pre-wrap">
                    {selectedContact.message}
                  </p>
                </div>
              </div>

              {/* Date */}
              <div className="text-sm text-gray-500 flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                Received {new Date(selectedContact.created_at).toLocaleString()}
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t">
                <Button
                  className="flex-1 bg-brand-orange text-brand-navy"
                  onClick={() => window.open(`mailto:${selectedContact.email}?subject=Re: ${selectedContact.subject}`)}
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Reply via Email
                </Button>
                {selectedContact.phone && (
                  <Button
                    variant="outline"
                    onClick={() => window.open(`tel:${selectedContact.phone}`)}
                  >
                    <Phone className="w-4 h-4 mr-2" />
                    Call
                  </Button>
                )}
                <Button
                  variant="outline"
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => handleDelete(selectedContact.id)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
